import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  TablePagination,
  Typography,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import axios from "axios";
import { BASE_URL } from "../../../services/configUrls";
import { 
  Search as SearchIcon, 
  Close as CloseIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon
}
from "@mui/icons-material";
import { Snackbar, Alert } from "@mui/material";

const RenewalHistoryModal = ({ open, onClose }) => {
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState("");
  const [years, setYears] = useState([]);
  const [month, setMonth] = useState("");
  const [quarter, setQuarter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalCount, setTotalCount] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [snackbar, setSnackbar] = useState({
  open: false,
  message: "",
  severity: "info",
});

  // Invoice preview states
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState("");
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);
  const [invoicePreviewError, setInvoicePreviewError] = useState("");
  const [currentRenewalId, setCurrentRenewalId] = useState(null);
  const [invoiceLoadingId, setInvoiceLoadingId] = useState(null);

  // const handleViewInvoice = async (renewalId) => {
  //   setInvoiceLoadingId(renewalId);
  //   setCurrentRenewalId(renewalId);
  //   setInvoicePreviewOpen(true);
  //   setInvoicePreviewLoading(true);
  //   setInvoicePreviewError("");
  //   setInvoicePreviewUrl("");

  //   try {
  //     const response = await axios.get(
  //       `${BASE_URL}/accounts/get_invoices_renewal/${renewalId}`,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  //         },
  //       }
  //     );

  //     const url = response.data?.invoice_url;
  //     if (url) {
  //       setInvoicePreviewUrl(url);
  //     }else {
  //     setSnackbar({
  //       open: true,
  //       message: response.data?.detail || "No invoice available",
  //       severity: "warning",
  //     });
  //   }
  //   } catch (err) {
  //     console.error("Error fetching renewal invoice:", err);
  //     setInvoicePreviewError(
  //       err.response?.data?.detail ||
  //         err.response?.data?.message ||
  //         "Failed to fetch invoice"
  //     );
  //   } finally {
  //     setInvoiceLoadingId(null);
  //     setInvoicePreviewLoading(false);
  //   }
  // };

const handleViewInvoice = async (renewalId) => {
  setInvoiceLoadingId(renewalId);

  try {
    const response = await axios.get(
      `${BASE_URL}/accounts/get_invoices_renewal/${renewalId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      }
    );

    const url = response.data?.invoice_url;

    if (url) {
      setCurrentRenewalId(renewalId);
      setInvoicePreviewUrl(url);
      setInvoicePreviewOpen(true); // ✅ open ONLY if valid
    } else {
      setSnackbar({
        open: true,
        message: response.data?.detail,
        severity: "warning",
      });
    }

  }catch (err) {
  console.log("ERROR FULL:", err); // debug once

  const message =
    err.response?.data?.detail ||   // ✅ your backend message
    err.response?.data?.message ||
    "Failed to fetch invoice";

  setSnackbar({
    open: true,
    message,
    severity: "error",
  });
} finally {
    setInvoiceLoadingId(null);
  }
};

  const handleDownloadInvoice = async () => {
    if (!invoicePreviewUrl) return;

    try {
      const response = await axios.get(invoicePreviewUrl, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `Renewal_Invoice_${currentRenewalId || ""}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading renewal invoice:", err);
    }
  };

  const handleCloseInvoicePreview = () => {
    setInvoicePreviewOpen(false);
    setInvoicePreviewUrl("");
    setInvoicePreviewError("");
    setInvoicePreviewLoading(false);
    setCurrentRenewalId(null);
  };

  const fetchRenewals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/accounts/view_renewals`, {
        params: {
          year: year || undefined,
          month: month || undefined,
          quarter: quarter || undefined,
          page: page + 1,
          page_size: pageSize,
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });

      setAllRows(res.data.data || []);
      setTotalCount(res.data.total_count || 0);
      setYears(res.data.years || []);
    } catch (error) {
      console.error("Error fetching renewal history:", error);
      setAllRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchRenewals();
  }, [open, year, month, quarter, page, pageSize]);

  const filteredRows = useMemo(() => {
    const q = searchInput.toLowerCase().trim();
    if (!q) return allRows;

    return allRows.filter((row) =>
      [row.institute_name, row.membership_name, row.renewal_date]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q))
    );
  }, [allRows, searchInput]);

  const handleClose = () => {
    onClose();
    setYear("");
    setMonth("");
    setQuarter("");
    setPage(0);
    setPageSize(5);
    setSearchInput("");
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setYear("");
    setMonth("");
    setQuarter("");
    setPage(0);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          height: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">Renewal History</Typography>
        <IconButton aria-label="close" onClick={handleClose} sx={{ color: "#6B7280" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Search institute..."
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setPage(0);
            }}
            sx={{ width: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
                </InputAdornment>
              ),
            }}
          />

          <Select
            value={year}
            onChange={(e) => {
              setYear(e.target.value);
              setPage(0);
            }}
            displayEmpty
            size="small"
            renderValue={(selected) => (!selected ? "Year" : selected)}
          >
            {years.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>

          <Select
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              setPage(0);
            }}
            displayEmpty
            size="small"
            renderValue={(selected) => (!selected ? "Month" : selected)}
          >
            <MenuItem value="1">Jan</MenuItem>
            <MenuItem value="2">Feb</MenuItem>
            <MenuItem value="3">Mar</MenuItem>
            <MenuItem value="4">Apr</MenuItem>
            <MenuItem value="5">May</MenuItem>
            <MenuItem value="6">Jun</MenuItem>
            <MenuItem value="7">Jul</MenuItem>
            <MenuItem value="8">Aug</MenuItem>
            <MenuItem value="9">Sep</MenuItem>
            <MenuItem value="10">Oct</MenuItem>
            <MenuItem value="11">Nov</MenuItem>
            <MenuItem value="12">Dec</MenuItem>
          </Select>

          <Select
            value={quarter}
            onChange={(e) => {
              setQuarter(e.target.value);
              setPage(0);
            }}
            displayEmpty
            size="small"
            renderValue={(selected) => (!selected ? "Quarter" : selected)}
          >
            <MenuItem value="1">Q1</MenuItem>
            <MenuItem value="2">Q2</MenuItem>
            <MenuItem value="3">Q3</MenuItem>
            <MenuItem value="4">Q4</MenuItem>
          </Select>

          <Button variant="outlined" onClick={handleClearFilters}>
            Clear
          </Button>
        </Box>

        <TableContainer
          component={Paper}
          sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto" }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1053e3ff" }}>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Sl. No.
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Institute Name
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Membership Name
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Renewal Date
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Renewal No.
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Valid From
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Valid Till
                </TableCell>
                <TableCell sx={{ color: "#FFFFFF", fontWeight: 600, backgroundColor: "#1053e3ff" }}>
                  Invoice
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                    <TableCell colSpan={8} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2">No renewal history found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredRows.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{page * pageSize + index + 1}</TableCell> 
                    <TableCell>{row.institute_name}</TableCell>
                    <TableCell>{row.membership_name}</TableCell>
                    <TableCell>{row.renewal_date}</TableCell>
                    <TableCell>{row.renewal_number}</TableCell>
                    <TableCell>{row.valid_from}</TableCell>
                    <TableCell>{row.valid_till}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleViewInvoice(row.id)}
                        disabled={invoiceLoadingId === row.id}
                      >
                        {invoiceLoadingId === row.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 20, 50]}
          sx={{ flexShrink: 0 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="outlined" onClick={handleClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={invoicePreviewOpen}
      onClose={handleCloseInvoicePreview}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: "8px",
          height: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #E5E7EB",
          pb: 2,
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 600, color: "#1F1F1F" }}>
          Renewal Invoice Preview {currentRenewalId ? `- ${currentRenewalId}` : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadInvoice}
            disabled={!invoicePreviewUrl || invoicePreviewLoading}
            sx={{
              backgroundColor: "#10B981",
              "&:hover": { backgroundColor: "#059669" },
              "&.Mui-disabled": {
                backgroundColor: "#9CA3AF",
                color: "#FFFFFF",
              },
              textTransform: "none",
              fontSize: "12px",
              fontWeight: 500,
              boxShadow: "none",
            }}
          >
            Download
          </Button>
          <IconButton
            onClick={handleCloseInvoicePreview}
            size="small"
            sx={{
              color: "#6B7280",
              "&:hover": { backgroundColor: "#F3F4F6" },
            }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#F9FAFB",
          height: "100%",
        }}
      >
        {invoicePreviewLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
              Loading invoice...
            </Typography>
          </Box>
        ) : invoicePreviewError ? (
          <Typography sx={{ fontSize: "13px", color: "#EF4444" }}>
            {invoicePreviewError}
          </Typography>
        ) : invoicePreviewUrl ? (
          <iframe
            src={invoicePreviewUrl}
            style={{ width: "100%", height: "100%", border: "none" }}
            title="Renewal Invoice Preview"
          />
        ) : (
          <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
            No invoice available
          </Typography>
        )}
      </DialogContent>
    </Dialog>
    <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
    <Alert
      onClose={() => setSnackbar({ ...snackbar, open: false })}
      severity={snackbar.severity}
      variant="filled"
    >
      {snackbar.message}
    </Alert>
</Snackbar>
    </>
  );
};

export default RenewalHistoryModal;